import { Permissions } from "@/type/api";
import {
  ACCOUNTING_ROLES,
  DELIVERY_PARTNERS_ROLES,
  DRIVERS_ROLES,
  GAMES_ADMIN_ROLES,
  GAMES_REDEEM_ROLES,
  ACTION_KEYS,
  FULL_ACCESS_ROLES,
  MODULE_KEYS,
  PAGE_KEYS,
  SALES_ACCOUNTING_ROLES,
  WAREHOUSE_ROLES,
} from "./keys";
import type { AppModuleDefinition } from "./types";

export const businessModules: AppModuleDefinition[] = [
  {
    key: MODULE_KEYS.ECOMMERCE,
    label: "E-commerce",
    labelKey: "alkokh.sidebar.ecommerce",
    icon: "tabler-shopping-bag",
    fallbackRoles: [Permissions.Ecommerce],
    nav: {
      order: 50,
      titleKey: "alkokh.sidebar.ecommerce",
      icon: "tabler-shopping-bag",
    },
    pages: [
      {
        key: PAGE_KEYS.ECOMMERCE,
        label: "E-commerce",
        labelKey: "alkokh.sidebar.ecommerce",
        doctype: "Item",
        paths: ["/dashboards/ecommerce", "/ecommerce/category", "/ecommerce/mobile-home", "/ecommerce/mobile-home-banners", "/coupon"],
        pathPrefixes: ["/ecommerce/product/"],
        routeNames: ["dashboards-ecommerce", "ecommerce-product-list", "ecommerce-product-variants-template", "ecommerce-category", "ecommerce-mobile-home", "ecommerce-mobile-home-banners", "coupon"],
        fallbackRoles: [Permissions.Ecommerce],
        nav: {
          titleKey: "alkokh.sidebar.dashboard",
          icon: "tabler-layout-dashboard",
          order: 10,
          flattenChildren: true,
          children: [
            { titleKey: "alkokh.sidebar.dashboard", icon: "tabler-layout-dashboard", to: { name: "dashboards-ecommerce" }, order: 10 },
            { titleKey: "alkokh.sidebar.productList", icon: "tabler-package", to: { name: "ecommerce-product-list" }, order: 20 },
            { titleKey: "alkokh.sidebar.productCategory", icon: "tabler-category", to: { name: "ecommerce-category" }, order: 30 },
            { titleKey: "alkokh.sidebar.mobileHome", icon: "tabler-device-mobile", to: { name: "ecommerce-mobile-home" }, order: 35 },
            { titleKey: "alkokh.sidebar.coupon", icon: "tabler-ticket", to: { name: "coupon" }, order: 40 },
          ],
        },
      },
    ],
  },
  {
    key: MODULE_KEYS.ORDER,
    label: "Orders",
    labelKey: "alkokh.sidebar.order",
    icon: "tabler-shopping-cart",
    fallbackRoles: [Permissions.Order],
    nav: {
      order: 60,
      titleKey: "alkokh.sidebar.order",
      icon: "tabler-shopping-cart",
    },
    pages: [
      {
        key: PAGE_KEYS.ORDER_LIST,
        label: "Orders",
        labelKey: "alkokh.sidebar.orderList",
        doctype: "Sales Order",
        paths: ["/order/list"],
        pathPrefixes: ["/order/"],
        routeNames: ["order-list"],
        fallbackRoles: [Permissions.Order],
        nav: {
          titleKey: "alkokh.sidebar.orderList",
          icon: "tabler-clipboard-list",
          order: 10,
          to: { name: "order-list" },
        },
      },
    ],
  },
  {
    key: MODULE_KEYS.POS,
    label: "POS",
    labelKey: "alkokh.sidebar.pos",
    icon: "tabler-cash-register",
    fallbackRoles: SALES_ACCOUNTING_ROLES,
    nav: {
      order: 70,
      titleKey: "alkokh.sidebar.pos",
      icon: "tabler-cash-register",
      // Slot 4. Without this, Graveyard takes it by tying Pet at order 40 — an accident of array
      // position, not a decision. Point-of-sale is the fourth thing clinic staff actually touch.
      mobileOrder: 40,
      singlePage: true,
    },
    pages: [
      {
        key: PAGE_KEYS.POS_SELL,
        label: "POS Sell",
        labelKey: "alkokh.sidebar.pos",
        doctype: "Sales Invoice",
        paths: ["/pos", "/pos/sell"],
        pathPrefixes: ["/pos/"],
        fallbackRoles: SALES_ACCOUNTING_ROLES,
        nav: {
          titleKey: "alkokh.sidebar.pos",
          icon: "tabler-cash-register",
          order: 10,
          to: { path: "/pos" },
        },
      },
      {
        // ⚠️ A PERMISSION, NOT A SCREEN: no path, no menu entry. Granted per role in
        // /permission; whoever holds it may set or change a transport fare's price (the
        // management's fare list is otherwise fixed). Full access only by default.
        key: PAGE_KEYS.POS_TRANSPORT_FARE_PRICE,
        label: "POS: change transport fare price",
        labelKey: "alkokh.sidebar.posTransportFarePrice",
        fallbackRoles: [...FULL_ACCESS_ROLES],
      },
    ],
    actions: [
      { key: ACTION_KEYS.POS_SELL_CREATE_SALE, label: "Create POS sale", labelKey: "Create POS sale", pageKey: PAGE_KEYS.POS_SELL, fallbackRoles: SALES_ACCOUNTING_ROLES },
      { key: ACTION_KEYS.POS_SELL_CREATE_CUSTOMER, label: "Create POS customer", labelKey: "Create POS customer", pageKey: PAGE_KEYS.POS_SELL, fallbackRoles: SALES_ACCOUNTING_ROLES },
      { key: ACTION_KEYS.POS_SELL_PRINT, label: "Print POS receipt", labelKey: "Print POS receipt", pageKey: PAGE_KEYS.POS_SELL, fallbackRoles: SALES_ACCOUNTING_ROLES },
    ],
    shortcuts: [
      {
        icon: "tabler-cash-register",
        title: "POS",
        subtitle: "Sell & checkout",
        to: { path: "/pos" },
        accessKey: PAGE_KEYS.POS_SELL,
        order: 20,
      },
    ],
    search: [
      {
        group: "Operations",
        icon: "tabler-cash-register",
        title: "POS Sell",
        url: { path: "/pos" },
        accessKey: PAGE_KEYS.POS_SELL,
        order: 20,
      },
    ],
  },
  {
    key: MODULE_KEYS.ACCOUNTING,
    label: "Accounting",
    labelKey: "alkokh.sidebar.accounting",
    icon: "tabler-report-money",
    fallbackRoles: ACCOUNTING_ROLES,
    nav: {
      order: 80,
      titleKey: "alkokh.sidebar.accounting",
      icon: "tabler-report-money",
    },
    pages: [
      {
        key: PAGE_KEYS.ACCOUNTING_SALES_INVOICES,
        label: "Sales Invoices",
        labelKey: "alkokh.sidebar.salesInvoices",
        doctype: "Sales Invoice",
        paths: ["/accounting/sales-invoices"],
        pathPrefixes: ["/accounting/sales-invoices/"],
        fallbackRoles: SALES_ACCOUNTING_ROLES,
        nav: { titleKey: "alkokh.sidebar.salesInvoices", icon: "tabler-file-invoice", order: 10, to: { path: "/accounting/sales-invoices" } },
      },
      {
        key: PAGE_KEYS.ACCOUNTING_PAYMENT_ENTRIES,
        label: "Payment Entries",
        labelKey: "alkokh.sidebar.paymentEntries",
        doctype: "Payment Entry",
        paths: ["/accounting/payment-entries", "/accounting/payment-entries/pay", "/accounting/payment-entries/receive"],
        pathPrefixes: ["/accounting/payment-entries/"],
        fallbackRoles: ACCOUNTING_ROLES,
        nav: {
          titleKey: "alkokh.sidebar.paymentEntries",
          icon: "tabler-credit-card",
          order: 30,
          children: [
            { titleKey: "alkokh.sidebar.paymentEntriesAll", icon: "tabler-list", to: { path: "/accounting/payment-entries" }, order: 10 },
            { titleKey: "alkokh.sidebar.paymentEntriesPay", icon: "tabler-arrow-up-right", to: { path: "/accounting/payment-entries/pay" }, order: 20 },
            { titleKey: "alkokh.sidebar.paymentEntriesReceive", icon: "tabler-arrow-down-left", to: { path: "/accounting/payment-entries/receive" }, order: 30 },
          ],
        },
      },
      // Managers only: it compares cashiers with each other, so plain Accounts User is left out.
      { key: PAGE_KEYS.ACCOUNTING_SMART_DASHBOARD, label: "Smart Accounting", labelKey: "alkokh.sidebar.smartAccounting", doctype: "Sales Invoice", paths: ["/accounting/smart-dashboard"], fallbackRoles: [Permissions.AccountsManager, Permissions.Auditor, ...FULL_ACCESS_ROLES], nav: { titleKey: "alkokh.sidebar.smartAccounting", icon: "tabler-brain", order: 5, to: { path: "/accounting/smart-dashboard" } } },
      { key: PAGE_KEYS.ACCOUNTING_JOURNAL_ENTRIES, label: "Journal Entries", labelKey: "alkokh.sidebar.journalEntries", doctype: "Journal Entry", paths: ["/accounting/journal-entries"], pathPrefixes: ["/accounting/journal-entries/"], fallbackRoles: ACCOUNTING_ROLES, nav: { titleKey: "alkokh.sidebar.journalEntries", icon: "tabler-notebook", order: 40, to: { path: "/accounting/journal-entries" } } },
      { key: PAGE_KEYS.ACCOUNTING_CASHIERS, label: "Cashiers", labelKey: "alkokh.sidebar.cashiers", doctype: "POS Profile", paths: ["/accounting/cashiers"], pathPrefixes: ["/accounting/cashiers/"], fallbackRoles: ACCOUNTING_ROLES, nav: { titleKey: "alkokh.sidebar.cashiers", icon: "tabler-cash-register", order: 50, to: { path: "/accounting/cashiers" } } },
      { key: PAGE_KEYS.ACCOUNTING_GENERAL_LEDGER, label: "General Ledger", labelKey: "alkokh.sidebar.generalLedger", doctype: "GL Entry", paths: ["/accounting/general-ledger"], fallbackRoles: ACCOUNTING_ROLES, nav: { titleKey: "alkokh.sidebar.generalLedger", icon: "tabler-book", order: 60, to: { path: "/accounting/general-ledger" } } },
      { key: PAGE_KEYS.ACCOUNTING_ACCOUNT_TREE, label: "Account Tree", labelKey: "alkokh.sidebar.accountTree", doctype: "Account", paths: ["/accounting/account-tree"], fallbackRoles: ACCOUNTING_ROLES, nav: { titleKey: "alkokh.sidebar.accountTree", icon: "tabler-binary-tree", order: 70, to: { path: "/accounting/account-tree" } } },
      { key: PAGE_KEYS.ACCOUNTING_SETTINGS, label: "Accounting Settings", labelKey: "alkokh.sidebar.accountingSettings", doctype: "Account", paths: ["/accounting/settings"], fallbackRoles: ACCOUNTING_ROLES },
    ],
    actions: [
      { key: ACTION_KEYS.ACCOUNTING_SALES_INVOICES_CREATE, label: "Create sales invoice", labelKey: "Create sales invoice", pageKey: PAGE_KEYS.ACCOUNTING_SALES_INVOICES, fallbackRoles: SALES_ACCOUNTING_ROLES },
      { key: ACTION_KEYS.ACCOUNTING_SALES_INVOICES_UPDATE, label: "Update sales invoice", labelKey: "Update sales invoice", pageKey: PAGE_KEYS.ACCOUNTING_SALES_INVOICES, fallbackRoles: SALES_ACCOUNTING_ROLES },
      { key: ACTION_KEYS.ACCOUNTING_SALES_INVOICES_SUBMIT, label: "Submit sales invoice", labelKey: "Submit sales invoice", pageKey: PAGE_KEYS.ACCOUNTING_SALES_INVOICES, fallbackRoles: SALES_ACCOUNTING_ROLES },
      { key: ACTION_KEYS.ACCOUNTING_SALES_INVOICES_PRINT, label: "Print sales invoice", labelKey: "Print sales invoice", pageKey: PAGE_KEYS.ACCOUNTING_SALES_INVOICES, fallbackRoles: SALES_ACCOUNTING_ROLES },
      { key: ACTION_KEYS.ACCOUNTING_SALES_INVOICES_ADD_PAYMENT, label: "Add sales invoice payment", labelKey: "Add sales invoice payment", pageKey: PAGE_KEYS.ACCOUNTING_SALES_INVOICES, fallbackRoles: ACCOUNTING_ROLES },
      { key: ACTION_KEYS.ACCOUNTING_PAYMENT_ENTRIES_CREATE, label: "Create payment entry", labelKey: "Create payment entry", pageKey: PAGE_KEYS.ACCOUNTING_PAYMENT_ENTRIES, fallbackRoles: ACCOUNTING_ROLES },
      { key: ACTION_KEYS.ACCOUNTING_PAYMENT_ENTRIES_SUBMIT, label: "Submit payment entry", labelKey: "Submit payment entry", pageKey: PAGE_KEYS.ACCOUNTING_PAYMENT_ENTRIES, fallbackRoles: ACCOUNTING_ROLES },
      { key: ACTION_KEYS.ACCOUNTING_JOURNAL_ENTRIES_CREATE, label: "Create journal entry", labelKey: "Create journal entry", pageKey: PAGE_KEYS.ACCOUNTING_JOURNAL_ENTRIES, fallbackRoles: ACCOUNTING_ROLES },
      { key: ACTION_KEYS.ACCOUNTING_JOURNAL_ENTRIES_SUBMIT, label: "Submit journal entry", labelKey: "Submit journal entry", pageKey: PAGE_KEYS.ACCOUNTING_JOURNAL_ENTRIES, fallbackRoles: ACCOUNTING_ROLES },
      { key: ACTION_KEYS.ACCOUNTING_CASHIERS_CREATE, label: "Create cashier profile", labelKey: "Create cashier profile", pageKey: PAGE_KEYS.ACCOUNTING_CASHIERS, fallbackRoles: ACCOUNTING_ROLES },
    ],
    shortcuts: [
      { icon: "tabler-file-invoice", title: "Invoices", subtitle: "Sales invoices", to: { path: "/accounting/sales-invoices" }, accessKey: PAGE_KEYS.ACCOUNTING_SALES_INVOICES, order: 30 },
    ],
    search: [
      { group: "Operations", icon: "tabler-file-invoice", title: "Sales Invoices", url: { path: "/accounting/sales-invoices" }, accessKey: PAGE_KEYS.ACCOUNTING_SALES_INVOICES, order: 30 },
      { group: "Operations", icon: "tabler-brain", title: "Smart Accounting", url: { path: "/accounting/smart-dashboard" }, accessKey: PAGE_KEYS.ACCOUNTING_SMART_DASHBOARD, order: 31 },
    ],
  },
  {
    key: MODULE_KEYS.WAREHOUSE,
    label: "Warehouse",
    labelKey: "alkokh.sidebar.warehouse",
    icon: "tabler-building-warehouse",
    fallbackRoles: WAREHOUSE_ROLES,
    nav: { order: 90, titleKey: "alkokh.sidebar.warehouse", icon: "tabler-building-warehouse" },
    pages: [
      // Stock, sales, transfers and supplier rankings in one place; the same roles as the module.
      { key: PAGE_KEYS.WAREHOUSE_SMART_DASHBOARD, label: "Smart Warehouse", labelKey: "alkokh.sidebar.smartWarehouse", doctype: "Bin", paths: ["/warehouse/smart-dashboard"], fallbackRoles: WAREHOUSE_ROLES, nav: { titleKey: "alkokh.sidebar.smartWarehouse", icon: "tabler-brain", order: 5, to: { path: "/warehouse/smart-dashboard" } } },
      { key: PAGE_KEYS.WAREHOUSE_LIST, label: "Warehouses", labelKey: "alkokh.sidebar.warehouses", doctype: "Warehouse", paths: ["/warehouse"], pathPrefixes: ["/warehouse/"], fallbackRoles: WAREHOUSE_ROLES, nav: { titleKey: "alkokh.sidebar.warehouses", icon: "tabler-building-warehouse", order: 10, to: { path: "/warehouse" } } },
      { key: PAGE_KEYS.WAREHOUSE_ITEMS, label: "Warehouse Items", labelKey: "alkokh.sidebar.itemDefinitions", doctype: "Item", paths: ["/warehouse/items"], pathPrefixes: ["/warehouse/items/"], fallbackRoles: WAREHOUSE_ROLES, nav: { titleKey: "alkokh.sidebar.itemDefinitions", icon: "tabler-package", order: 20, to: { path: "/warehouse/items" } } },
      { key: PAGE_KEYS.WAREHOUSE_ITEM_PRICES, label: "Item Prices", labelKey: "alkokh.sidebar.itemPrices", doctype: "Item Price", paths: ["/warehouse/item-prices"], pathPrefixes: ["/warehouse/item-prices/"], fallbackRoles: WAREHOUSE_ROLES, nav: { titleKey: "alkokh.sidebar.itemPrices", icon: "tabler-tag", order: 22, to: { path: "/warehouse/item-prices" } } },
      // Sits beside Item Prices at 22, NOT with the stock-count group at 34: it is a
      // pricing tool that writes no ledger and cannot change what stock is worth.
      // `fallbackRoles` is deliberately narrower than WAREHOUSE_ROLES — only a stock
      // manager may rewrite what things cost.
      { key: PAGE_KEYS.WAREHOUSE_STOCK_PRICES, label: "Price Updates", labelKey: "alkokh.sidebar.stockPrices", doctype: "Stock Price", paths: ["/warehouse/stock-prices"], pathPrefixes: ["/warehouse/stock-prices/"], fallbackRoles: [Permissions.StockManager, ...FULL_ACCESS_ROLES], nav: { titleKey: "alkokh.sidebar.stockPrices", icon: "tabler-report-money", order: 23, to: { path: "/warehouse/stock-prices" } } },
      { key: PAGE_KEYS.ITEM_UNITS_LIST, label: "Item Units", labelKey: "alkokh.sidebar.itemUnits", doctype: "UOM", paths: ["/warehouse/item-units"], pathPrefixes: ["/warehouse/item-units/"], fallbackRoles: WAREHOUSE_ROLES, nav: { titleKey: "alkokh.sidebar.itemUnits", icon: "tabler-ruler", order: 25, to: { path: "/warehouse/item-units" } } },
      { key: PAGE_KEYS.WAREHOUSE_STOCK_ENTRIES, label: "Stock Entries", labelKey: "alkokh.sidebar.stockEntries", doctype: "Stock Entry", paths: ["/warehouse/stock-entries"], pathPrefixes: ["/warehouse/stock-entries/"], fallbackRoles: [Permissions.StockUser, Permissions.StockManager, ...FULL_ACCESS_ROLES], nav: { titleKey: "alkokh.sidebar.stockEntries", icon: "tabler-transfer", order: 30, to: { path: "/warehouse/stock-entries" } } },
      { key: PAGE_KEYS.WAREHOUSE_STOCK_TRANSFERS, label: "Stock Transfer", labelKey: "alkokh.sidebar.stockTransfers", doctype: "Stock Transfer Order", paths: ["/warehouse/stock-transfers"], pathPrefixes: ["/warehouse/stock-transfers/"], fallbackRoles: [Permissions.StockUser, Permissions.StockManager, ...FULL_ACCESS_ROLES], nav: { titleKey: "alkokh.sidebar.stockTransfers", icon: "tabler-truck-delivery", order: 32, to: { path: "/warehouse/stock-transfers" } } },
      { key: PAGE_KEYS.WAREHOUSE_STOCK_RECONCILIATIONS, label: "Stock Counts", labelKey: "alkokh.sidebar.stockReconciliations", doctype: "Stock Reconciliation", paths: ["/warehouse/stock-reconciliations"], pathPrefixes: ["/warehouse/stock-reconciliations/"], fallbackRoles: [Permissions.StockUser, Permissions.StockManager, ...FULL_ACCESS_ROLES], nav: { titleKey: "alkokh.sidebar.stockReconciliations", icon: "tabler-scale", order: 34, to: { path: "/warehouse/stock-reconciliations" } } },
      { key: PAGE_KEYS.WAREHOUSE_STOCK_COUNT, label: "Stocktaking", labelKey: "alkokh.sidebar.stockCount", doctype: "Stock Entry", paths: ["/warehouse/stock-count"], fallbackRoles: [Permissions.StockUser, Permissions.StockManager, ...FULL_ACCESS_ROLES], nav: { titleKey: "alkokh.sidebar.stockCount", icon: "tabler-clipboard-list", order: 35, to: { path: "/warehouse/stock-count" } } },
      { key: PAGE_KEYS.WAREHOUSE_STOCK_CHECKER, label: "Stock Checker", labelKey: "alkokh.sidebar.stockChecker", doctype: "Bin", paths: ["/warehouse/stock-checker"], fallbackRoles: [Permissions.StockUser, Permissions.StockManager, ...FULL_ACCESS_ROLES], nav: { titleKey: "alkokh.sidebar.stockChecker", icon: "tabler-scan", order: 36, to: { path: "/warehouse/stock-checker" } } },
      // Read-only monitor of POS returns and exchanges; the returns records are readable by Administrator until the backend grants read (docs/README.pos-returns-cart-backend-prompt.md).
      { key: PAGE_KEYS.WAREHOUSE_RETURNS_MONITOR, label: "Returns Monitor", labelKey: "alkokh.sidebar.returnsMonitor", doctype: "Sales Invoice", paths: ["/warehouse/returns-monitor"], fallbackRoles: [Permissions.StockManager, Permissions.AccountsManager, ...FULL_ACCESS_ROLES], nav: { titleKey: "alkokh.sidebar.returnsMonitor", icon: "tabler-arrow-back-up", order: 38, to: { path: "/warehouse/returns-monitor" } } },
      { key: PAGE_KEYS.WAREHOUSE_STOCK_LEDGER, label: "Stock Ledger", labelKey: "alkokh.sidebar.stockLedger", doctype: "Stock Ledger Entry", paths: ["/warehouse/stock-ledger"], fallbackRoles: [Permissions.StockUser, Permissions.StockManager, ...FULL_ACCESS_ROLES], nav: { titleKey: "alkokh.sidebar.stockLedger", icon: "tabler-book", order: 40, to: { path: "/warehouse/stock-ledger" } } },
      { key: PAGE_KEYS.WAREHOUSE_PURCHASE_INVOICES, label: "Purchase Invoices", labelKey: "alkokh.sidebar.purchaseInvoices", doctype: "Purchase Invoice", paths: ["/warehouse/purchase-invoices"], pathPrefixes: ["/warehouse/purchase-invoices/"], fallbackRoles: WAREHOUSE_ROLES, nav: { titleKey: "alkokh.sidebar.purchaseInvoices", icon: "tabler-file-invoice", order: 50, to: { path: "/warehouse/purchase-invoices" } } },
      {
        key: PAGE_KEYS.ACCOUNTING_SUPPLIERS,
        label: "Suppliers",
        labelKey: "alkokh.sidebar.suppliers",
        doctype: "Supplier",
        paths: ["/warehouse/suppliers", "/suppliers"],
        pathPrefixes: ["/warehouse/suppliers/", "/suppliers/", "/supplier/"],
        routeNames: ["suppliers"],
        fallbackRoles: [Permissions.Supplier, Permissions.StockUser, Permissions.StockManager, Permissions.AccountsUser, Permissions.AccountsManager, Permissions.PurchaseUser, Permissions.PurchaseManager, ...FULL_ACCESS_ROLES],
        nav: { titleKey: "alkokh.sidebar.suppliers", icon: "tabler-truck-delivery", order: 60, to: { path: "/warehouse/suppliers" } },
      },
    ],
    actions: [
      { key: ACTION_KEYS.WAREHOUSE_CREATE, label: "Create warehouse", labelKey: "Create warehouse", pageKey: PAGE_KEYS.WAREHOUSE_LIST, fallbackRoles: [Permissions.StockManager, ...FULL_ACCESS_ROLES] },
      { key: ACTION_KEYS.WAREHOUSE_ITEMS_CREATE, label: "Create warehouse item", labelKey: "Create warehouse item", pageKey: PAGE_KEYS.WAREHOUSE_ITEMS, fallbackRoles: [Permissions.ItemManager, Permissions.StockManager, ...FULL_ACCESS_ROLES] },
      { key: ACTION_KEYS.WAREHOUSE_ITEMS_UPDATE, label: "Update warehouse item", labelKey: "Update warehouse item", pageKey: PAGE_KEYS.WAREHOUSE_ITEMS, fallbackRoles: [Permissions.ItemManager, Permissions.StockManager, ...FULL_ACCESS_ROLES] },
      { key: ACTION_KEYS.WAREHOUSE_ITEMS_DELETE, label: "Delete warehouse item", labelKey: "Delete warehouse item", pageKey: PAGE_KEYS.WAREHOUSE_ITEMS, fallbackRoles: [Permissions.ItemManager, Permissions.StockManager, ...FULL_ACCESS_ROLES] },
      { key: ACTION_KEYS.WAREHOUSE_ITEM_PRICES_CREATE, label: "Create item price", labelKey: "Create item price", pageKey: PAGE_KEYS.WAREHOUSE_ITEM_PRICES, fallbackRoles: [Permissions.ItemManager, Permissions.StockManager, ...FULL_ACCESS_ROLES] },
      { key: ACTION_KEYS.WAREHOUSE_ITEM_PRICES_UPDATE, label: "Update item price", labelKey: "Update item price", pageKey: PAGE_KEYS.WAREHOUSE_ITEM_PRICES, fallbackRoles: [Permissions.ItemManager, Permissions.StockManager, ...FULL_ACCESS_ROLES] },
      { key: ACTION_KEYS.WAREHOUSE_ITEM_PRICES_DELETE, label: "Delete item price", labelKey: "Delete item price", pageKey: PAGE_KEYS.WAREHOUSE_ITEM_PRICES, fallbackRoles: [Permissions.ItemManager, Permissions.StockManager, ...FULL_ACCESS_ROLES] },
      { key: ACTION_KEYS.WAREHOUSE_STOCK_PRICES_CREATE, label: "Create price update", labelKey: "Create price update", pageKey: PAGE_KEYS.WAREHOUSE_STOCK_PRICES, fallbackRoles: [Permissions.StockManager, ...FULL_ACCESS_ROLES] },
      { key: ACTION_KEYS.WAREHOUSE_STOCK_PRICES_SUBMIT, label: "Apply price update", labelKey: "Apply price update", pageKey: PAGE_KEYS.WAREHOUSE_STOCK_PRICES, fallbackRoles: [Permissions.StockManager, ...FULL_ACCESS_ROLES] },
      { key: ACTION_KEYS.WAREHOUSE_STOCK_PRICES_CANCEL, label: "Undo price update", labelKey: "Undo price update", pageKey: PAGE_KEYS.WAREHOUSE_STOCK_PRICES, fallbackRoles: [Permissions.StockManager, ...FULL_ACCESS_ROLES] },
      { key: ACTION_KEYS.WAREHOUSE_PURCHASE_INVOICES_CREATE, label: "Create purchase invoice", labelKey: "Create purchase invoice", pageKey: PAGE_KEYS.WAREHOUSE_PURCHASE_INVOICES, fallbackRoles: WAREHOUSE_ROLES },
      { key: ACTION_KEYS.WAREHOUSE_PURCHASE_INVOICES_SUBMIT, label: "Submit purchase invoice", labelKey: "Submit purchase invoice", pageKey: PAGE_KEYS.WAREHOUSE_PURCHASE_INVOICES, fallbackRoles: WAREHOUSE_ROLES },
      { key: ACTION_KEYS.WAREHOUSE_PURCHASE_INVOICES_ADD_PAYMENT, label: "Add purchase payment", labelKey: "Add purchase payment", pageKey: PAGE_KEYS.WAREHOUSE_PURCHASE_INVOICES, fallbackRoles: ACCOUNTING_ROLES },
      { key: ACTION_KEYS.WAREHOUSE_STOCK_ENTRIES_CREATE, label: "Create stock entry", labelKey: "Create stock entry", pageKey: PAGE_KEYS.WAREHOUSE_STOCK_ENTRIES, fallbackRoles: [Permissions.StockUser, Permissions.StockManager, ...FULL_ACCESS_ROLES] },
      { key: ACTION_KEYS.WAREHOUSE_STOCK_ENTRIES_SUBMIT, label: "Submit stock entry", labelKey: "Submit stock entry", pageKey: PAGE_KEYS.WAREHOUSE_STOCK_ENTRIES, fallbackRoles: [Permissions.StockManager, ...FULL_ACCESS_ROLES] },
      { key: ACTION_KEYS.WAREHOUSE_STOCK_RECONCILIATIONS_CREATE, label: "Create stock count", labelKey: "Create stock count", pageKey: PAGE_KEYS.WAREHOUSE_STOCK_RECONCILIATIONS, fallbackRoles: [Permissions.StockUser, Permissions.StockManager, ...FULL_ACCESS_ROLES] },
      // ⚠️ Manager-only, unlike create. Posting a count writes the stock ledger and
      // cannot be undone, so it is deliberately a narrower grant than counting is —
      // the same split as submitting a stock entry.
      { key: ACTION_KEYS.WAREHOUSE_STOCK_RECONCILIATIONS_SUBMIT, label: "Post stock count", labelKey: "Post stock count", pageKey: PAGE_KEYS.WAREHOUSE_STOCK_RECONCILIATIONS, fallbackRoles: [Permissions.StockManager, ...FULL_ACCESS_ROLES] },
      { key: ACTION_KEYS.ITEM_UNITS_CREATE, label: "Create item unit", labelKey: "Create item unit", pageKey: PAGE_KEYS.ITEM_UNITS_LIST, fallbackRoles: [Permissions.ItemManager, Permissions.StockManager, ...FULL_ACCESS_ROLES] },
      { key: ACTION_KEYS.ITEM_UNITS_UPDATE, label: "Update item unit", labelKey: "Update item unit", pageKey: PAGE_KEYS.ITEM_UNITS_LIST, fallbackRoles: [Permissions.ItemManager, Permissions.StockManager, ...FULL_ACCESS_ROLES] },
      { key: ACTION_KEYS.ITEM_UNITS_DELETE, label: "Delete item unit", labelKey: "Delete item unit", pageKey: PAGE_KEYS.ITEM_UNITS_LIST, fallbackRoles: [Permissions.ItemManager, Permissions.StockManager, ...FULL_ACCESS_ROLES] },
    ],
    shortcuts: [
      { icon: "tabler-package", title: "Items", subtitle: "Stock & services", to: { path: "/warehouse/items" }, accessKey: PAGE_KEYS.WAREHOUSE_ITEMS, order: 40 },
    ],
    search: [
      { group: "Records", icon: "tabler-package", title: "Items", url: { path: "/warehouse/items" }, accessKey: PAGE_KEYS.WAREHOUSE_ITEMS, order: 40, noData: true },
      { group: "Records", icon: "tabler-tag", title: "Item Prices", url: { path: "/warehouse/item-prices" }, accessKey: PAGE_KEYS.WAREHOUSE_ITEM_PRICES, order: 42, noData: true },
      { group: "Records", icon: "tabler-report-money", title: "Price Updates", url: { path: "/warehouse/stock-prices" }, accessKey: PAGE_KEYS.WAREHOUSE_STOCK_PRICES, order: 43, noData: true },
      { group: "Operations", icon: "tabler-brain", title: "Smart Warehouse", url: { path: "/warehouse/smart-dashboard" }, accessKey: PAGE_KEYS.WAREHOUSE_SMART_DASHBOARD, order: 39 },
      { group: "Operations", icon: "tabler-building-warehouse", title: "Warehouse", url: { path: "/warehouse" }, accessKey: PAGE_KEYS.WAREHOUSE_LIST, order: 40 },
      { group: "Records", icon: "tabler-truck-delivery", title: "Suppliers", url: { path: "/warehouse/suppliers" }, accessKey: PAGE_KEYS.ACCOUNTING_SUPPLIERS, order: 45, noData: true },
      { group: "Records", icon: "tabler-ruler-2", title: "Item Units", url: { path: "/warehouse/item-units" }, accessKey: PAGE_KEYS.ITEM_UNITS_LIST, order: 46, noData: true },
    ],
  },
  /**
   * Drivers — delivery orders, dispatch, van sales, settlement.
   *
   * ⚠️ Registering a page key GATES the page. `/drivers/:id` was reachable by anyone before
   * only because, being orphaned, it had no key to fail. On a backend-driven deployment the
   * keys must be synced and granted from `/permission` before anyone but a full-access
   * bootstrap user can open them — see the note on `PAGE_KEYS.DRIVERS_LIST`.
   *
   * Pages for orders / dispatch / settlement / dashboard are added here as each one ships,
   * so the sidebar never links to a route that does not exist yet. Driver CRUD stays in
   * Settings → Drivers.
   */
  {
    key: MODULE_KEYS.DRIVERS,
    label: "Drivers",
    labelKey: "alkokh.sidebar.drivers",
    icon: "tabler-truck-delivery",
    fallbackRoles: DRIVERS_ROLES,
    nav: {
      order: 76,
      titleKey: "alkokh.sidebar.drivers",
      icon: "tabler-truck-delivery",
    },
    pages: [
      {
        key: PAGE_KEYS.DRIVERS_LIST,
        label: "Drivers",
        labelKey: "alkokh.sidebar.drivers",
        doctype: "Driver",
        paths: ["/drivers"],
        routeNames: ["drivers"],
        fallbackRoles: DRIVERS_ROLES,
        nav: {
          titleKey: "alkokh.driverOrders.nav.drivers",
          icon: "tabler-truck-delivery",
          order: 10,
          to: { path: "/drivers" },
        },
      },
      {
        key: PAGE_KEYS.DRIVERS_ORDERS,
        label: "Delivery orders",
        labelKey: "alkokh.driverOrders.orders.title",
        doctype: "Sales Order",
        paths: ["/drivers/orders"],
        routeNames: ["drivers-orders"],
        fallbackRoles: DRIVERS_ROLES,
        nav: {
          titleKey: "alkokh.driverOrders.orders.title",
          icon: "tabler-clipboard-list",
          order: 20,
          to: { path: "/drivers/orders" },
        },
      },
      {
        key: PAGE_KEYS.DRIVERS_DISPATCH,
        label: "Dispatch",
        labelKey: "alkokh.driverOrders.dispatch.pageTitle",
        doctype: "Stock Entry",
        paths: ["/drivers/dispatch"],
        routeNames: ["drivers-dispatch"],
        fallbackRoles: DRIVERS_ROLES,
        nav: {
          titleKey: "alkokh.driverOrders.dispatch.pageTitle",
          icon: "tabler-package",
          order: 25,
          to: { path: "/drivers/dispatch" },
        },
      },
      {
        key: PAGE_KEYS.DRIVERS_SETTLEMENT,
        label: "Driver settlement",
        labelKey: "alkokh.driverOrders.settlement.title",
        doctype: "Payment Entry",
        paths: ["/drivers/settlement"],
        routeNames: ["drivers-settlement"],
        fallbackRoles: DRIVERS_ROLES,
        nav: {
          titleKey: "alkokh.driverOrders.settlement.title",
          icon: "tabler-wallet",
          order: 30,
          to: { path: "/drivers/settlement" },
        },
      },
      {
        key: PAGE_KEYS.DRIVERS_DASHBOARD,
        label: "Drivers dashboard",
        labelKey: "alkokh.driverOrders.dashboard.title",
        paths: ["/drivers/dashboard"],
        routeNames: ["drivers-dashboard"],
        fallbackRoles: DRIVERS_ROLES,
        nav: {
          titleKey: "alkokh.driverOrders.dashboard.title",
          icon: "tabler-chart-bar",
          order: 40,
          to: { path: "/drivers/dashboard" },
        },
      },
      {
        // Listed by route name AND exact path, so the `/drivers/` prefix of the detail
        // entry below can never claim it (`getPageKeyForRoute` checks names first).
        key: PAGE_KEYS.DRIVERS_ME,
        label: "My deliveries",
        labelKey: "alkokh.driverSelf.title",
        paths: ["/drivers/me"],
        routeNames: ["drivers-me"],
        fallbackRoles: DRIVERS_ROLES,
        nav: {
          titleKey: "alkokh.driverSelf.title",
          icon: "tabler-scan",
          order: 5,
          to: { path: "/drivers/me" },
        },
      },
      {
        key: PAGE_KEYS.DRIVERS_DETAIL,
        label: "Driver",
        labelKey: "alkokh.driverHub.detailTitle",
        doctype: "Driver",
        pathPrefixes: ["/drivers/"],
        routeNames: ["drivers-id"],
        fallbackRoles: DRIVERS_ROLES,
      },
    ],
    shortcuts: [
      { icon: "tabler-truck-delivery", title: "Drivers", subtitle: "Deliveries & settlement", to: { path: "/drivers" }, accessKey: PAGE_KEYS.DRIVERS_LIST, order: 45 },
    ],
    search: [
      { group: "Operations", icon: "tabler-truck-delivery", title: "Drivers", url: { path: "/drivers" }, accessKey: PAGE_KEYS.DRIVERS_LIST, order: 47 },
    ],
  },
  /**
   * Delivery partners — the aggregator apps (Talabat, Toters …) that list our products,
   * take the customer's money, and remit the month's takings minus their commission.
   *
   * Sits beside Drivers because both are "someone else delivered it", but they are not
   * the same shape: a driver holds OUR cash and settles it back, a partner holds a
   * RECEIVABLE and pays it down. Nothing in this module moves stock.
   *
   * ⚠️ Registering these keys GATES the pages — see the note on
   * `PAGE_KEYS.DELIVERY_PARTNERS_LIST`. Sync and grant from `/permission` before
   * announcing them.
   */
  {
    key: MODULE_KEYS.DELIVERY_PARTNERS,
    label: "Delivery Partners",
    labelKey: "alkokh.sidebar.deliveryPartners",
    icon: "tabler-building-store",
    fallbackRoles: DELIVERY_PARTNERS_ROLES,
    nav: {
      order: 77,
      titleKey: "alkokh.sidebar.deliveryPartners",
      icon: "tabler-building-store",
    },
    pages: [
      {
        key: PAGE_KEYS.DELIVERY_PARTNERS_LIST,
        label: "Delivery Partners",
        labelKey: "alkokh.sidebar.deliveryPartners",
        doctype: "Delivery Partner",
        paths: ["/delivery-partners"],
        routeNames: ["delivery-partners"],
        fallbackRoles: DELIVERY_PARTNERS_ROLES,
        nav: {
          titleKey: "alkokh.deliveryPartners.list.title",
          icon: "tabler-building-store",
          order: 10,
          to: { path: "/delivery-partners" },
        },
      },
      {
        // No `nav` — a detail page is reached from the list, never from the sidebar.
        // `pathPrefixes` is what covers `/delivery-partners/<id>` under one key.
        key: PAGE_KEYS.DELIVERY_PARTNERS_DETAIL,
        label: "Delivery Partner",
        labelKey: "alkokh.deliveryPartners.detail.title",
        doctype: "Delivery Partner",
        pathPrefixes: ["/delivery-partners/"],
        routeNames: ["delivery-partners-id"],
        fallbackRoles: DELIVERY_PARTNERS_ROLES,
      },
    ],
    shortcuts: [
      { icon: "tabler-building-store", title: "Delivery Partners", subtitle: "App orders & settlement", to: { path: "/delivery-partners" }, accessKey: PAGE_KEYS.DELIVERY_PARTNERS_LIST, order: 46 },
    ],
    search: [
      { group: "Operations", icon: "tabler-building-store", title: "Delivery Partners", url: { path: "/delivery-partners" }, accessKey: PAGE_KEYS.DELIVERY_PARTNERS_LIST, order: 48 },
    ],
  },
  /**
   * Kiosk games (Play & Win) — the campaigns, prizes and claim codes behind the
   * customer kiosk app in `alkok-shop-game/`. Managers design games and prizes;
   * reception and the tills only get "Redeem prize code".
   *
   * Runs on the demo store until the backend ships (`src/api/gamesApi.ts`,
   * `docs/backend/kiosk-games.md`).
   *
   * ⚠️ Registering these keys GATES the pages — see the note on
   * `PAGE_KEYS.GAMES_LIST`. Sync and grant from `/permission` before announcing them.
   */
  {
    key: MODULE_KEYS.GAMES,
    label: "Games",
    labelKey: "alkokh.sidebar.games",
    icon: "tabler-trophy",
    fallbackRoles: GAMES_REDEEM_ROLES,
    nav: {
      order: 78,
      titleKey: "alkokh.sidebar.games",
      icon: "tabler-trophy",
    },
    pages: [
      {
        // ⚠️ An exact path, not a prefix: `/games/` (the detail page) would otherwise claim it.
        key: PAGE_KEYS.GAMES_DASHBOARD,
        label: "Games dashboard",
        labelKey: "alkokh.games.dashboard.title",
        doctype: "Kiosk Game Play",
        paths: ["/games/dashboard"],
        routeNames: ["games-dashboard"],
        fallbackRoles: GAMES_ADMIN_ROLES,
        nav: {
          titleKey: "alkokh.games.dashboard.title",
          icon: "tabler-chart-bar",
          order: 5,
          to: { path: "/games/dashboard" },
        },
      },
      {
        key: PAGE_KEYS.GAMES_LIST,
        label: "Games",
        labelKey: "alkokh.games.list.title",
        doctype: "Kiosk Game",
        paths: ["/games"],
        routeNames: ["games"],
        fallbackRoles: GAMES_ADMIN_ROLES,
        nav: {
          titleKey: "alkokh.games.list.title",
          icon: "tabler-trophy",
          order: 10,
          to: { path: "/games" },
        },
      },
      {
        // Reached from the list. `/games/prizes` etc. match by exact path first,
        // so this prefix only ever catches a game id.
        key: PAGE_KEYS.GAMES_DETAIL,
        label: "Game",
        labelKey: "alkokh.games.detail.title",
        doctype: "Kiosk Game",
        pathPrefixes: ["/games/"],
        routeNames: ["games-id"],
        fallbackRoles: GAMES_ADMIN_ROLES,
      },
      {
        key: PAGE_KEYS.GAMES_PRIZES,
        label: "Prizes",
        labelKey: "alkokh.games.prizes.title",
        doctype: "Kiosk Game Prize",
        paths: ["/games/prizes"],
        routeNames: ["games-prizes"],
        fallbackRoles: GAMES_ADMIN_ROLES,
        nav: {
          titleKey: "alkokh.games.prizes.title",
          icon: "tabler-gift",
          order: 20,
          to: { path: "/games/prizes" },
        },
      },
      {
        key: PAGE_KEYS.GAMES_PLAYS,
        label: "Plays",
        labelKey: "alkokh.games.plays.title",
        doctype: "Kiosk Game Play",
        paths: ["/games/plays"],
        routeNames: ["games-plays"],
        fallbackRoles: GAMES_ADMIN_ROLES,
        nav: {
          titleKey: "alkokh.games.plays.title",
          icon: "tabler-history",
          order: 30,
          to: { path: "/games/plays" },
        },
      },
      {
        key: PAGE_KEYS.GAMES_REDEEM,
        label: "Redeem prize code",
        labelKey: "alkokh.games.redeem.title",
        doctype: "Kiosk Game Play",
        paths: ["/games/redeem"],
        routeNames: ["games-redeem"],
        fallbackRoles: GAMES_REDEEM_ROLES,
        nav: {
          titleKey: "alkokh.games.redeem.title",
          icon: "tabler-qrcode",
          order: 40,
          to: { path: "/games/redeem" },
        },
      },
    ],
    shortcuts: [
      { icon: "tabler-qrcode", title: "Redeem prize code", subtitle: "Kiosk game prizes", to: { path: "/games/redeem" }, accessKey: PAGE_KEYS.GAMES_REDEEM, order: 47 },
    ],
    search: [
      { group: "Operations", icon: "tabler-trophy", title: "Games", url: { path: "/games" }, accessKey: PAGE_KEYS.GAMES_LIST, order: 49 },
      { group: "Operations", icon: "tabler-qrcode", title: "Redeem prize code", url: { path: "/games/redeem" }, accessKey: PAGE_KEYS.GAMES_REDEEM, order: 50 },
    ],
  },
];
